/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RefreshAuthTokenService } from '../../../../services/Auth';
import { decodeJwt } from '../../../../utils/tokenUtil';
import { internalUserEmailValidate } from '../../../../utils/formatStrings';
import { ApplicationState } from '../../../../store';
import {
  refreshAuthTokenRequest,
  refreshAuthTokenSuccess,
  updateAuthStateSuccess
} from '../../../../store/ducks/auth/actions';
import {
  IRefreshAuthToken,
  RENEW_TOKEN_INTERVAL_MILLISECONDS
} from '../../../../store/ducks/auth/types';
import ModalContinueSession from '../../ModalContinueSession';
import { userLogout } from '../../../../store/ducks/user/actions';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import ModalEndSession from '../../ModalEndSession';
import ModalLogin from '../../Login';

import { useLocation } from 'react-router-dom';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { UserService } from '../../../../services/User';
import { ClockManager } from '../../UserSession/ClockManager';

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timer: NodeJS.Timeout;

  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const DynamicTokenRenewal: React.FC = () => {
  const dispatch = useDispatch();
  const userEmail = useSelector((state: ApplicationState) => state.user?.email);
  const refreshAuthTokenService = new RefreshAuthTokenService();
  const userService = new UserService();
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [errorRequest, setErrorRequest] = useState(false);
  const [showModalContinueSession, setShowModalContinueSession] =
    useState(false);
  const [showModalEndSession, setShowModalEndSession] = useState(false);
  const [showModalLogin, setShowModalLogin] = useState(false);
  const finalPeriodOfValidity = 300000; // equivalente a 5 minutos
  const preExpiration = 360000;
  const downtime = 900000; // equivalente a 15 minutos
  const getTokenTimes = () => {
    const token = decodeJwt(refreshAuthTokenService.getToken() ?? '');
    const securityToken = decodeJwt(
      refreshAuthTokenService.getSecurityToken() ?? ''
    );
    const currentTime = Date.now();
    const timeRemainingToken = (token?.expiration ?? 0) - currentTime;
    const timeRemainingSecurityToken =
      (securityToken?.expiration ?? 0) - currentTime;

    return {
      accessTokenRemaining: timeRemainingToken,
      securityTokenRemaining: timeRemainingSecurityToken,
      securityTokenCreated: securityToken?.createdAt ?? 0
    };
  };
  let tokenTimes = getTokenTimes();
  const oneMinute = 60000;
  const oneSecond = 1000;
  const [validationPeriod, setValidationPeriod] = useState(() => oneMinute);

  const [modalName, setModalName] = useState<
    'prestes_a_expirar' | 'sessao_expirada'
  >('sessao_expirada');

  const location = useLocation();

  useEffect(() => {
    if (errorRequest)
      dispatch(
        addToast({
          message: 'Ocorreu um erro inesperado durante a requisição.',
          type: EToastTypes.DANGER,
          durationInMS: 5000,
          spaceToTop: 77
        })
      );
  }, [errorRequest]);

  const token = decodeJwt(refreshAuthTokenService.getToken() ?? '');
  const tokenTotalLife = Math.round(
    ((token?.expiration ?? 0) - (token?.createdAt ?? 0)) / 1000 / 60 / 60
  );

  const checkTokenExpiration = () => {
    tokenTimes = getTokenTimes();

    // vida total recalculada a cada checagem (o token pode ter sido renovado desde o último render)
    const currentToken = decodeJwt(refreshAuthTokenService.getToken() ?? '');
    const tokenTotalLifeHours = Math.round(
      ((currentToken?.expiration ?? 0) - (currentToken?.createdAt ?? 0)) /
        1000 /
        60 /
        60
    );

    // Sessão interna (SSO ou domínio Gerdau) nunca deve exigir reautenticação por senha;
    // para esses usuários o fluxo é sempre o de renovação (continuar sessão).
    const isInternalSession =
      refreshAuthTokenService.getSsoAccess() ||
      internalUserEmailValidate(userEmail ?? '');

    // Sessão externa: qualquer token expirado (access_token de 24h OU Azure/security token de 60min)
    // exige reautenticação, pois o que expirar primeiro já impede o uso da aplicação
    const externalTokenExpired =
      !isInternalSession &&
      (tokenTimes.accessTokenRemaining <= 0 ||
        tokenTimes.securityTokenRemaining <= 0);

    if (externalTokenExpired && !showModalLogin) {
      setShowModalLogin(true);
      setShowModalContinueSession(false);
      setModalName('sessao_expirada');
      setValidationPeriod(oneMinute);
      return;
    }

    // Renovação: sessão interna (qualquer duração) ou externa com token curto (1h..24h),
    // quando o security token está prestes a expirar
    const isRenewableSession =
      isInternalSession ||
      (tokenTotalLifeHours >= 1 && tokenTotalLifeHours < 24);

    if (
      isRenewableSession &&
      tokenTimes.securityTokenRemaining > 0 &&
      tokenTimes.securityTokenRemaining <= finalPeriodOfValidity &&
      !showModalContinueSession
    ) {
      setShowModalContinueSession(true);
      setModalName('prestes_a_expirar');
      setValidationPeriod(oneMinute);
      return;
    }

    // garante que o modal seja exibido a partir de 5 minutos, depois remove checagem a cada segundo
    const securityTokenIsAboutToExpire =
      tokenTimes.securityTokenRemaining > 0 &&
      tokenTimes.securityTokenRemaining <= preExpiration;

    if (securityTokenIsAboutToExpire) {
      if (
        tokenTimes.securityTokenRemaining > finalPeriodOfValidity &&
        validationPeriod !== oneSecond
      ) {
        setValidationPeriod(oneSecond);
      }
    }

    const accessTokenIsAboutToExpire =
      tokenTimes.accessTokenRemaining > 0 &&
      tokenTimes.accessTokenRemaining < oneMinute * 2;

    if (
      accessTokenIsAboutToExpire &&
      validationPeriod !== oneSecond &&
      !showModalLogin
    ) {
      setValidationPeriod(oneSecond);
    }
  };

  const dispatchRefreshToken = () => {
    tokenTimes = getTokenTimes();

    const timeAfterCreationOfTheSecurityToken =
      Date.now() - tokenTimes.securityTokenCreated;

    if (
      tokenTimes.securityTokenRemaining > finalPeriodOfValidity &&
      timeAfterCreationOfTheSecurityToken > downtime
    ) {
      dispatch(refreshAuthTokenRequest());
      return;
    }
  };

  const handleUserActivity = () => {
    dispatchRefreshToken();
  };

  const debouncedHandleUserActivity = debounce(
    handleUserActivity,
    5 * oneSecond
  );

  useEffect(() => {
    window.addEventListener('focus', debouncedHandleUserActivity);
    window.addEventListener('blur', debouncedHandleUserActivity);
    window.addEventListener('click', debouncedHandleUserActivity);
    window.addEventListener('scroll', debouncedHandleUserActivity);
    window.addEventListener('keydown', debouncedHandleUserActivity);
    window.addEventListener('mousemove', debouncedHandleUserActivity);

    const intervalId = setInterval(checkTokenExpiration, validationPeriod);

    return () => {
      window.removeEventListener('focus', debouncedHandleUserActivity);
      window.removeEventListener('blur', debouncedHandleUserActivity);
      window.removeEventListener('scroll', debouncedHandleUserActivity);
      window.removeEventListener('mousemove', debouncedHandleUserActivity);
      window.removeEventListener('click', debouncedHandleUserActivity);
      window.removeEventListener('keydown', debouncedHandleUserActivity);
      clearInterval(intervalId);
    };
  }, [validationPeriod]);

  const cancelSession = () => {
    dispatch(
      sendTagging({
        event: 'encerrar_sessao',
        screenName: location.pathname,
        ecommerce: {
          sessionStatus: 'prestes_a_expirar'
        }
      })
    );
    setShowModalContinueSession(false);
    setShowModalEndSession(true);
  };

  const cancelSessionWithoutTagging = () => {
    setShowModalContinueSession(false);
    setShowModalEndSession(true);
  };

  const refreshToken = async () => {
    setLoadingRequest(true);
    setErrorRequest(false);
    try {
      const response: IRefreshAuthToken =
        await refreshAuthTokenService.refreshAuthToken();

      const azureDateTimeExpiration =
        new Date().getTime() + RENEW_TOKEN_INTERVAL_MILLISECONDS;

      RefreshAuthTokenService.setAuthorizationToken(response.access_token);
      RefreshAuthTokenService.setAuthorizationTokenExpiration(
        azureDateTimeExpiration.toString()
      );

      dispatch(
        refreshAuthTokenSuccess(
          response.access_token,
          response.expires_in,
          azureDateTimeExpiration
        )
      );

      dispatch(
        updateAuthStateSuccess(
          response.access_token,
          Number(response.expires_in),
          azureDateTimeExpiration
        )
      );

      setShowModalContinueSession(false);
    } catch (error) {
      setErrorRequest(true);
    } finally {
      setLoadingRequest(false);
    }
  };

  const activeTokens = () => {
    return (
      tokenTimes.securityTokenRemaining > 0 &&
      tokenTimes.accessTokenRemaining > 0
    );
  };

  const logout = async () => {
    try {
      const tokensAreActive = activeTokens();
      if (tokensAreActive) {
        setLoadingRequest(true);
        await userService.userLogout();
        setLoadingRequest(false);
        return;
      }

      await userService.userLogout();

      dispatch(userLogout(false));
      setShowModalEndSession(false);

      if (!tokensAreActive) {
        userService.logout();
      }
    } catch (error) {
      // Há cenários aonde o token expira antes do logout. O erro não faz sentido.
    } finally {
      setLoadingRequest(false);
    }
  };

  if (showModalContinueSession) {
    return (
      <ModalContinueSession
        onCancelClick={cancelSession}
        onContinueClick={refreshToken}
        loading={loadingRequest}
      />
    );
  }

  if (showModalEndSession) {
    return (
      <ModalEndSession
        actionClick={logout}
        loading={loadingRequest}
        modalName={modalName}
      />
    );
  }

  if (showModalLogin) {
    return (
      <ModalLogin
        exit={cancelSessionWithoutTagging}
        close={() => setShowModalLogin(false)}
        refreshToken={refreshToken}
      ></ModalLogin>
    );
  }

  return (
    <ClockManager
      showModalRefreshToken={() =>
        tokenTotalLife >= 24
          ? setShowModalLogin(true)
          : setShowModalContinueSession(true)
      }
    />
  );
};

export default DynamicTokenRenewal;
